import { getSDK, getUserProfileCached, invalidateUserDataCache } from '../../shared/functions/sdk.js'

async function loadProfile (root) {
  try {
    const profile = await getUserProfileCached()
    root.update({
      accountProfile: profile || null,
      accountError: null,
      accountLoading: false
    })
  } catch (err) {
    root.update({
      accountProfile: null,
      accountError: err?.message || 'Failed to load profile',
      accountLoading: false
    })
  }
}

async function saveProfile (root) {
  const { accountDraft } = root
  if (!accountDraft) return
  root.update({ accountSaving: true, accountSaveError: null, accountSaveOk: false })
  try {
    const sdk = getSDK()
    await sdk.updateUser?.({
      name: accountDraft.name,
      email: accountDraft.email
    })
    invalidateUserDataCache?.()
    root.update({
      accountSaving: false,
      accountSaveOk: true,
      accountProfile: { ...(root.accountProfile || {}), ...accountDraft }
    })
    // Clear success banner after 3 s
    setTimeout(() => root.update({ accountSaveOk: false }), 3000)
  } catch (err) {
    root.update({
      accountSaving: false,
      accountSaveError: err?.message || 'Failed to save changes'
    })
  }
}

async function changePassword (root) {
  const { accountCurrentPassword, accountNewPassword } = root
  if (!accountCurrentPassword || !accountNewPassword) return
  root.update({ passwordSaving: true, passwordError: null, passwordOk: false })
  try {
    const sdk = getSDK()
    await sdk.updatePassword?.({
      currentPassword: accountCurrentPassword,
      newPassword: accountNewPassword
    })
    root.update({
      passwordSaving: false,
      passwordOk: true,
      accountCurrentPassword: '',
      accountNewPassword: ''
    })
    setTimeout(() => root.update({ passwordOk: false }), 3000)
  } catch (err) {
    root.update({
      passwordSaving: false,
      passwordError: err?.message || 'Failed to change password'
    })
  }
}

const fieldRow = (labelText, inputConfig) => {
  const inputId = inputConfig.id || ('account-' + (inputConfig.autocomplete || labelText).replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '').toLowerCase())
  return {
    flow: 'y',
    gap: 'V',
    Label: {
      tag: 'label',
      text: labelText,
      fontSize: 'Z',
      color: 'caption',
      fontWeight: '500',
      attr: { for: inputId }
    },
    Input: {
      tag: 'input',
      id: inputId,
      padding: 'Y Z1',
      round: 'Z',
      border: '1px solid line',
      background: 'overlay',
      color: 'title',
      fontSize: 'Z1',
      width: '100%',
      ...inputConfig
    }
  }
}

export const account = {
  tag: 'main',
  id: 'main-content',
  extends: 'Flex',
  flow: 'y',
  align: 'start center',
  gridRow: '2',
  gridColumn: '1 / -1',
  minHeight: '0',
  height: '100%',
  overflowY: 'auto',
  background: 'document',
  padding: 'D C',

  metadata: { title: 'Account / Symbols.app' },

  onRender: (el, s) => {
    if (el.__accountLoaded) return
    el.__accountLoaded = true
    const root = s.root || s
    const sdk = getSDK()
    const token = sdk?.getAuthToken?.()
    if (!token) {
      el.router('/signin', el.getRoot())
      return
    }
    root.update({ accountLoading: true })
    loadProfile(root)
  },

  Container: {
    flow: 'y',
    gap: 'C',
    width: '100%',
    maxWidth: '540px',

    TopBar: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Y',

      BackBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Y',
        round: 'Z',
        border: '0',
        background: 'overlay',
        color: 'caption',
        fontSize: 'Z',
        cursor: 'pointer',
        ':hover': { background: 'overlayStrong', color: 'title' },
        text: '← Dashboard',
        onClick: (e, el, s) => el.router('/dashboard', el.getRoot())
      }
    },

    PageHeading: {
      flow: 'y',
      gap: 'V',
      H1: {
        tag: 'h1',
        text: 'Account',
        fontSize: 'C',
        fontWeight: '700',
        margin: '0',
        color: 'title',
        letterSpacing: '-0.02em'
      },
      Subtitle: {
        tag: 'p',
        text: 'Manage your profile and account settings.',
        fontSize: 'Z1',
        color: 'caption',
        margin: '0'
      }
    },

    LoadingState: {
      if: (el, s) => !!(s.root?.accountLoading),
      tag: 'p',
      text: 'Loading…',
      color: 'caption',
      fontSize: 'Z1'
    },

    ErrorState: {
      if: (el, s) => !!(s.root?.accountError) && !s.root?.accountLoading,
      tag: 'p',
      text: (el, s) => s.root?.accountError,
      color: 'red',
      fontSize: 'Z'
    },

    ProfileSection: {
      if: (el, s) => !!(s.root?.accountProfile) && !s.root?.accountLoading,
      flow: 'y',
      gap: 'B',
      padding: 'B',
      round: 'A',
      border: '1px solid line',
      background: 'overlay',

      ProfileSectionTitle: {
        tag: 'h2',
        text: 'Profile',
        fontSize: 'A',
        fontWeight: '600',
        margin: '0',
        color: 'title'
      },

      NameRow: {
        ...fieldRow('Full name', {
          type: 'text',
          autocomplete: 'name',
          placeholder: 'Your name',
          'aria-label': 'Full name',
          value: (el, s) =>
            s.root?.accountDraft?.name ??
            s.root?.accountProfile?.name ??
            '',
          onInput: (e, el, s) => {
            const root = s.root || s
            const draft = root.accountDraft || { ...root.accountProfile }
            root.update({ accountDraft: { ...draft, name: e.target.value } })
          }
        })
      },

      EmailRow: {
        ...fieldRow('Email address', {
          type: 'email',
          autocomplete: 'email',
          placeholder: 'you@example.com',
          'aria-label': 'Email address',
          value: (el, s) =>
            s.root?.accountDraft?.email ??
            s.root?.accountProfile?.email ??
            '',
          onInput: (e, el, s) => {
            const root = s.root || s
            const draft = root.accountDraft || { ...root.accountProfile }
            root.update({ accountDraft: { ...draft, email: e.target.value } })
          }
        })
      },

      SaveFeedback: {
        if: (el, s) => !!(s.root?.accountSaveOk),
        tag: 'p',
        text: 'Changes saved.',
        color: 'green',
        fontSize: 'Z',
        margin: '0',
        role: 'status'
      },

      SaveError: {
        if: (el, s) => !!(s.root?.accountSaveError),
        tag: 'p',
        text: (el, s) => s.root?.accountSaveError,
        color: 'red',
        fontSize: 'Z',
        margin: '0',
        role: 'alert'
      },

      SaveBtn: {
        tag: 'button',
        type: 'submit',
        text: (el, s) => s.root?.accountSaving ? 'Saving…' : 'Save changes',
        padding: 'Y A',
        round: 'Z',
        border: '0',
        background: 'blue',
        color: 'white',
        fontSize: 'Z1',
        fontWeight: '500',
        cursor: 'pointer',
        alignSelf: 'start',
        ':hover': { background: 'blue+8' },
        disabled: (el, s) => !!s.root?.accountSaving,
        onClick: (e, el, s) => saveProfile(s.root || s)
      }
    },

    PasswordSection: {
      if: (el, s) => !!(s.root?.accountProfile) && !s.root?.accountLoading,
      flow: 'y',
      gap: 'B',
      padding: 'B',
      round: 'A',
      border: '1px solid line',
      background: 'overlay',

      PasswordSectionTitle: {
        tag: 'h2',
        text: 'Change password',
        fontSize: 'A',
        fontWeight: '600',
        margin: '0',
        color: 'title'
      },

      CurrentPasswordRow: {
        ...fieldRow('Current password', {
          type: 'password',
          autocomplete: 'current-password',
          placeholder: '••••••••',
          'aria-label': 'Current password',
          value: (el, s) => s.root?.accountCurrentPassword || '',
          onInput: (e, el, s) =>
            (s.root || s).update({ accountCurrentPassword: e.target.value })
        })
      },

      NewPasswordRow: {
        ...fieldRow('New password', {
          type: 'password',
          autocomplete: 'new-password',
          placeholder: '••••••••',
          'aria-label': 'New password',
          value: (el, s) => s.root?.accountNewPassword || '',
          onInput: (e, el, s) =>
            (s.root || s).update({ accountNewPassword: e.target.value })
        })
      },

      PasswordFeedback: {
        if: (el, s) => !!(s.root?.passwordOk),
        tag: 'p',
        text: 'Password updated.',
        color: 'green',
        fontSize: 'Z',
        margin: '0',
        role: 'status'
      },

      PasswordError: {
        if: (el, s) => !!(s.root?.passwordError),
        tag: 'p',
        text: (el, s) => s.root?.passwordError,
        color: 'red',
        fontSize: 'Z',
        margin: '0',
        role: 'alert'
      },

      PasswordBtn: {
        tag: 'button',
        type: 'submit',
        text: (el, s) => s.root?.passwordSaving ? 'Updating…' : 'Update password',
        padding: 'Y A',
        round: 'Z',
        border: '1px solid line',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z1',
        fontWeight: '500',
        cursor: 'pointer',
        alignSelf: 'start',
        ':hover': { background: 'overlayStrong' },
        disabled: (el, s) => !!s.root?.passwordSaving,
        onClick: (e, el, s) => changePassword(s.root || s)
      }
    },

    SignOutSection: {
      if: (el, s) => !!(s.root?.accountProfile) && !s.root?.accountLoading,
      flow: 'y',
      gap: 'B',
      padding: 'B',
      round: 'A',
      border: '1px solid line',
      background: 'overlay',

      SignOutTitle: {
        tag: 'h2',
        text: 'Sign out',
        fontSize: 'A',
        fontWeight: '600',
        margin: '0',
        color: 'title'
      },

      SignOutHint: {
        tag: 'p',
        text: 'Sign out of your account on this device.',
        fontSize: 'Z',
        color: 'caption',
        margin: '0'
      },

      SignOutBtn: {
        tag: 'button',
        type: 'button',
        text: 'Sign out',
        padding: 'Y A',
        round: 'Z',
        border: '1px solid line',
        background: 'transparent',
        color: 'title',
        fontSize: 'Z1',
        fontWeight: '500',
        cursor: 'pointer',
        alignSelf: 'start',
        ':hover': { background: 'overlay', color: 'red' },
        onClick: (e, el, s) => {
          const sdk = getSDK()
          sdk?.logout?.()
          el.router('/signin', el.getRoot())
        }
      }
    }
  }
}
